import { createFileRoute, Link } from "@tanstack/react-router";
import { usePageTitle } from "@/hooks/use-page-title";
import { stripePromise } from "@/lib/stripe";
import { apiClient } from "@/lib/api/client";
import type {
  CheckoutSessionResponse,
  CheckoutPlanInfo,
} from "@/lib/api/types";
import {
  CheckoutProvider,
  PaymentElement,
  useCheckout,
} from "@stripe/react-stripe-js/checkout";
import type { Appearance } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "next-themes";
import {
  ArrowLeft,
  AlertCircle,
  Shield,
  Loader2,
  Check,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { MotionConfig, motion } from "motion/react";
import { useQuery } from "@tanstack/react-query";

function getStripeAppearance(isDark: boolean): Appearance {
  if (isDark) {
    return {
      theme: "night",
      variables: {
        colorPrimary: "#f43f5e",
        colorBackground: "#141416",
        colorText: "#fafafa",
        colorTextSecondary: "#a3a3a3",
        colorDanger: "#ef4444",
        borderRadius: "0.5rem",
        fontFamily: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
        spacingUnit: "4px",
        colorIcon: "#a3a3a3",
        colorIconHover: "#fafafa",
      },
      rules: {
        ".Input": {
          border: "1px solid hsl(240 4% 14%)",
          boxShadow: "none",
          backgroundColor: "hsl(240 5% 8.5%)",
          transition: "border-color 150ms ease, box-shadow 150ms ease",
        },
        ".Input:focus": {
          border: "1px solid #f43f5e",
          boxShadow: "0 0 0 1px rgba(244, 63, 94, 0.25)",
        },
        ".Input:hover": {
          border: "1px solid hsl(240 4% 20%)",
        },
        ".Label": {
          fontSize: "0.8125rem",
          fontWeight: "500",
          color: "#a3a3a3",
          letterSpacing: "0.01em",
        },
        ".Tab": {
          border: "1px solid hsl(240 4% 14%)",
          backgroundColor: "hsl(240 5% 8.5%)",
          color: "#a3a3a3",
          transition: "all 150ms ease",
        },
        ".Tab--selected": {
          border: "1px solid #f43f5e",
          backgroundColor: "rgba(244, 63, 94, 0.06)",
          color: "#fafafa",
        },
        ".Tab:hover": {
          border: "1px solid hsl(240 4% 20%)",
          color: "#fafafa",
        },
        ".Block": {
          border: "1px solid hsl(240 4% 14%)",
          backgroundColor: "hsl(240 5% 8.5%)",
        },
      },
    };
  }

  return {
    theme: "stripe",
    variables: {
      colorPrimary: "#e11d48",
      colorBackground: "#ffffff",
      colorText: "#0a0a0a",
      colorTextSecondary: "#737373",
      colorDanger: "#dc2626",
      borderRadius: "0.5rem",
      fontFamily: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
      spacingUnit: "4px",
    },
    rules: {
      ".Input": {
        border: "1px solid #e5e5e5",
        boxShadow: "none",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
      },
      ".Input:focus": {
        border: "1px solid #e11d48",
        boxShadow: "0 0 0 1px rgba(225, 29, 72, 0.25)",
      },
      ".Input:hover": {
        border: "1px solid #d4d4d4",
      },
      ".Label": {
        fontSize: "0.8125rem",
        fontWeight: "500",
      },
      ".Tab": {
        border: "1px solid #e5e5e5",
      },
      ".Tab--selected": {
        border: "1px solid #e11d48",
        backgroundColor: "rgba(225, 29, 72, 0.04)",
      },
    },
  };
}

export const Route = createFileRoute("/dashboard/checkout/")({
  component: CheckoutPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tenantId: search.tenantId as string,
    plan: (search.plan as string) || "pro",
  }),
});

function CheckoutPage() {
  usePageTitle("Checkout");
  const { tenantId, plan: targetPlan } = Route.useSearch();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const { data, error, isPending } = useQuery({
    queryKey: ["checkout-session", tenantId, targetPlan],
    queryFn: () =>
      apiClient
        .post<CheckoutSessionResponse>(
          `/api/tenants/${tenantId}/subscription/checkout`,
          { plan: targetPlan }
        )
        .then((res) => res.data.data),
    retry: false,
    staleTime: Infinity,
  });

  const appearance = useMemo(() => getStripeAppearance(isDark), [isDark]);

  if (isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-6 animate-spin text-rose-500" />
          <p className="text-muted-foreground text-sm">Preparing checkout...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    const message =
      error instanceof Error
        ? error.message
        : "Failed to create checkout session";
    return (
      <MotionConfig reducedMotion="user">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="mx-auto max-w-lg space-y-6 py-12"
        >
          <Button variant="ghost" size="sm" asChild className="gap-1.5">
            <Link
              to="/dashboard/teams/$tenantId"
              params={{ tenantId }}
              search={{ tab: "subscription" }}
            >
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
          <Card>
            <CardContent className="flex flex-col items-center py-12 text-center">
              <div className="bg-destructive/10 mb-4 flex size-12 items-center justify-center rounded-full">
                <AlertCircle className="text-destructive size-6" />
              </div>
              <h2 className="mb-2 text-balance text-lg font-semibold">
                Checkout Unavailable
              </h2>
              <p className="text-muted-foreground mb-6 max-w-sm text-pretty text-sm">
                {message}
              </p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </MotionConfig>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="mx-auto max-w-5xl py-8"
      >
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="text-muted-foreground mb-4 gap-1.5"
          >
            <Link
              to="/dashboard/teams/$tenantId"
              params={{ tenantId }}
              search={{ tab: "subscription" }}
            >
              <ArrowLeft className="size-3.5" />
              Back to Subscription
            </Link>
          </Button>
          <h1 className="font-heading text-2xl font-bold">
            Upgrade to {data.plan.name}
          </h1>
          <p className="text-muted-foreground mt-1 text-pretty text-sm">
            Review your plan and complete payment
          </p>
        </div>

        <CheckoutProvider
          stripe={stripePromise}
          options={{
            clientSecret: data.clientSecret,
            elementsOptions: { appearance },
          }}
        >
          <ThemeSync />
          <CheckoutContent plan={data.plan} />
        </CheckoutProvider>
      </motion.div>
    </MotionConfig>
  );
}

function ThemeSync() {
  const { resolvedTheme } = useTheme();
  const checkout = useCheckout();

  useEffect(() => {
    if (checkout.type !== "success") return;
    checkout.checkout.changeAppearance(
      getStripeAppearance(resolvedTheme === "dark")
    );
  }, [resolvedTheme, checkout]);

  return null;
}

function CheckoutContent({ plan }: { plan: CheckoutPlanInfo }) {
  const checkout = useCheckout();

  if (checkout.type === "loading") {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-6 animate-spin text-rose-500" />
          <p className="text-muted-foreground text-sm">Loading payment...</p>
        </div>
      </div>
    );
  }

  if (checkout.type === "error") {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 text-center">
          <div className="bg-destructive/10 mb-4 flex size-12 items-center justify-center rounded-full">
            <AlertCircle className="text-destructive size-6" />
          </div>
          <h2 className="mb-2 text-balance text-lg font-semibold">
            Checkout Unavailable
          </h2>
          <p className="text-muted-foreground mb-6 max-w-sm text-pretty text-sm">
            {checkout.error.message}
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const lineItem = checkout.checkout.lineItems[0];
  const total = lineItem?.total;
  const recurring = lineItem?.recurring;

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      {/* Payment form — primary focus */}
      <div className="lg:col-span-7">
        <CheckoutForm planName={plan.name} />
      </div>

      {/* Order summary sidebar */}
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-8">
          <Card>
            <CardContent className="p-6">
              <h2 className="font-heading mb-4 text-sm font-semibold">
                Order Summary
              </h2>

              <div className="mb-4 flex items-baseline justify-between">
                <span className="text-sm">{plan.name} Plan</span>
                {total && (
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-2xl font-bold tabular-nums">
                      {total.amount}
                    </span>
                    {recurring && (
                      <span className="text-muted-foreground text-xs">
                        /{recurring.interval}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Separator className="mb-4" />

              <p className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wider">
                What's included
              </p>
              <ul className="space-y-2.5">
                {plan.highlights.map((label, i) => (
                  <motion.li
                    key={label}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.1 + i * 0.04,
                      ease: "easeOut",
                    }}
                    className="flex items-center gap-2.5"
                  >
                    <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-rose-500/10">
                      <Check className="size-3 text-rose-500" />
                    </div>
                    <span className="text-sm">{label}</span>
                  </motion.li>
                ))}
              </ul>

              <Separator className="my-4" />

              <div className="space-y-2">
                {[
                  "Cancel anytime, no lock-in",
                  "Instant access after payment",
                ].map((text) => (
                  <p
                    key={text}
                    className="text-muted-foreground flex items-center gap-2 text-xs"
                  >
                    <Shield className="size-3 shrink-0" />
                    {text}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function CheckoutForm({ planName }: { planName: string }) {
  const checkout = useCheckout();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (checkout.type !== "success") return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);

    const result = await checkout.checkout.confirm();

    if (result.type === "error") {
      setErrorMessage(result.error.message);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardContent className="space-y-6 p-6">
          <div>
            <h2 className="font-heading text-base font-semibold">
              Payment Details
            </h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              All transactions are encrypted and secure
            </p>
          </div>

          <PaymentElement />

          {errorMessage && (
            <div className="text-destructive border-destructive/20 bg-destructive/5 flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm">
              <AlertCircle className="size-4 shrink-0" />
              {errorMessage}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-rose-500 text-white hover:bg-rose-600"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Lock className="mr-2 size-3.5" />
                Subscribe to {planName}
              </>
            )}
          </Button>

          <p className="text-muted-foreground/60 text-center text-[11px]">
            Payment securely processed by Stripe. By subscribing you agree to
            recurring billing.
          </p>
        </CardContent>
      </Card>
    </form>
  );
}
